import React, { useCallback, useEffect, useState } from 'react';
import crmClient from './crmClient';

const titleize = value =>
  String(value || '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const emptyForm = { name: '', email: '', phone: '', organization: '', amount: '', notes: '', stage: '' };

const PipelineBoardView = ({ pipeline, meta, title, subtitle, showAmount, showOrganization }) => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const stagesFromMeta = (meta && meta.pipeline_stages && meta.pipeline_stages[pipeline]) || [];

  const load = useCallback(() => {
    setLoading(true);
    crmClient
      .pipelineBoard(pipeline)
      .then(payload => {
        setBoard(payload);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load the pipeline.');
        setLoading(false);
      });
  }, [pipeline]);

  useEffect(() => {
    load();
  }, [load]);

  const stages = (board && board.stages) || stagesFromMeta;
  const counts = (board && board.counts) || {};
  const records = (board && board.records) || [];

  const recordsByStage = stage => records.filter(record => record.stage === stage);

  const submit = () => {
    setBusy(true);
    setError(null);
    crmClient
      .createPipelineRecord(pipeline, { ...form, stage: form.stage || stages[0] })
      .then(() => {
        setBusy(false);
        setForm(emptyForm);
        setShowForm(false);
        load();
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to add record.');
      });
  };

  const moveStage = (record, stage) => {
    setBusy(true);
    crmClient
      .updatePipelineRecord(pipeline, record.crm_pipeline_record_id, { stage })
      .then(() => {
        setBusy(false);
        load();
      })
      .catch(() => {
        setBusy(false);
        setError('Unable to move record.');
      });
  };

  const remove = record => {
    setBusy(true);
    crmClient
      .deletePipelineRecord(pipeline, record.crm_pipeline_record_id)
      .then(() => {
        setBusy(false);
        load();
      })
      .catch(() => {
        setBusy(false);
        setError('Unable to remove record.');
      });
  };

  return (
    <div className="crm-pipeline-board">
      <div className="crm-calllist-head">
        <div>
          <h2>{title}</h2>
          <p className="crm-muted">{subtitle}</p>
        </div>
        <button type="button" className="crm-btn" onClick={() => setShowForm(prev => !prev)}>
          {showForm ? 'Cancel' : `Add ${pipeline === 'investor' ? 'investor' : 'driver'}`}
        </button>
      </div>

      {error && <div className="crm-inline-error">{error}</div>}

      {showForm && (
        <div className="crm-card crm-form">
          <div className="crm-field-row">
            <label>
              <span>Name</span>
              <input value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} />
            </label>
            {showOrganization && (
              <label>
                <span>Organization / firm</span>
                <input value={form.organization} onChange={event => setForm({ ...form, organization: event.target.value })} />
              </label>
            )}
          </div>
          <div className="crm-field-row">
            <label>
              <span>Email</span>
              <input type="email" value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} />
            </label>
            <label>
              <span>Phone</span>
              <input value={form.phone} onChange={event => setForm({ ...form, phone: event.target.value })} />
            </label>
            {showAmount && (
              <label>
                <span>Amount ($)</span>
                <input type="number" value={form.amount} onChange={event => setForm({ ...form, amount: event.target.value })} />
              </label>
            )}
            <label>
              <span>Stage</span>
              <select value={form.stage} onChange={event => setForm({ ...form, stage: event.target.value })}>
                {stages.map(stage => (
                  <option key={stage} value={stage}>
                    {titleize(stage)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button type="button" className="crm-btn" disabled={busy || (!form.name && !form.email)} onClick={submit}>
            Save
          </button>
        </div>
      )}

      {loading && <div className="crm-empty">Loading pipeline…</div>}

      {!loading && (
        <div className="crm-board">
          {stages.map(stage => (
            <div className="crm-board-col" key={stage}>
              <div className="crm-board-col-head">
                <span>{titleize(stage)}</span>
                <span className="crm-board-count">{counts[stage] || 0}</span>
              </div>
              <div className="crm-board-cards">
                {recordsByStage(stage).map(record => (
                  <div className="crm-board-card" key={record.crm_pipeline_record_id}>
                    <strong>{record.name || record.email || 'Unnamed'}</strong>
                    {showOrganization && record.organization && <small>{record.organization}</small>}
                    {record.email && <small>{record.email}</small>}
                    {showAmount && record.amount != null && (
                      <small>${Number(record.amount).toLocaleString('en-US')}</small>
                    )}
                    <div className="crm-board-card-actions">
                      <select value={record.stage} disabled={busy} onChange={event => moveStage(record, event.target.value)}>
                        {stages.map(option => (
                          <option key={option} value={option}>
                            {titleize(option)}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="crm-icon-btn" title="Remove" onClick={() => remove(record)}>
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
                {recordsByStage(stage).length === 0 && <div className="crm-board-empty">—</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PipelineBoardView;
